<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import naviOsMcpConnectionsAPI from 'dashboard/api/naviOsMcpConnections';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();

const scopeOptions = [
  'pipeline:read',
  'pipeline:write',
  'contacts:read',
  'contacts:pii',
  'conversations:read',
  'conversations:write',
  'knowledge:read',
  'tasks:read',
  'tasks:write',
  'reports:read',
];

const isLoading = ref(true);
const activeAction = ref('');
const connections = ref([]);
const issuedToken = ref('');
const approvals = ref([]);
const availableInboxes = ref([]);
const availableFunnels = ref([]);
const allowlistsInitialized = ref(false);
let refreshTimer;
const form = ref({
  name: 'Navi OS',
  installation_id: '',
  scopes: [
    'pipeline:read',
    'contacts:read',
    'conversations:read',
    'knowledge:read',
    'tasks:read',
    'reports:read',
  ],
  allowed_inbox_ids: [],
  allowed_funnel_ids: [],
});

const endpoint = computed(() => `${window.location.origin}/mcp`);
const hasConnections = computed(() => connections.value.length > 0);
const formatArguments = argumentsValue =>
  JSON.stringify(argumentsValue, null, 2);

const errorMessage = (error, fallbackKey) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(fallbackKey);

const loadConnections = async () => {
  try {
    const { data } = await naviOsMcpConnectionsAPI.get();
    connections.value = data.connections || [];
    availableInboxes.value = data.inboxes || [];
    availableFunnels.value = data.funnels || [];
    if (!allowlistsInitialized.value) {
      form.value.allowed_inbox_ids = availableInboxes.value.map(
        item => item.id
      );
      form.value.allowed_funnel_ids = availableFunnels.value.map(
        item => item.id
      );
      allowlistsInitialized.value = true;
    }
    const responses = await Promise.all(
      connections.value.map(connection =>
        naviOsMcpConnectionsAPI.approvals(connection.id)
      )
    );
    approvals.value = responses.flatMap(({ data: approvalData }, index) =>
      (approvalData.approvals || []).map(approval => ({
        ...approval,
        connectionId: connections.value[index].id,
      }))
    );
  } catch (error) {
    useAlert(
      errorMessage(error, 'INTEGRATION_SETTINGS.NAVI_OS_MCP.API.LOAD_ERROR')
    );
  }
};

const decideApproval = async (approval, decision) => {
  activeAction.value = `${decision}-${approval.id}`;
  try {
    await naviOsMcpConnectionsAPI[decision](approval.connectionId, approval.id);
    useAlert(
      decision === 'approve'
        ? t('INTEGRATION_SETTINGS.NAVI_OS_MCP.API.APPROVE_SUCCESS')
        : t('INTEGRATION_SETTINGS.NAVI_OS_MCP.API.REJECT_SUCCESS')
    );
    await loadConnections();
  } catch (error) {
    useAlert(
      errorMessage(error, 'INTEGRATION_SETTINGS.NAVI_OS_MCP.API.ACTION_ERROR')
    );
  } finally {
    activeAction.value = '';
  }
};

onMounted(async () => {
  try {
    await loadConnections();
  } finally {
    isLoading.value = false;
  }
  refreshTimer = window.setInterval(loadConnections, 30000);
});

onBeforeUnmount(() => window.clearInterval(refreshTimer));

const copy = async (value, messageKey) => {
  await navigator.clipboard.writeText(value);
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  useAlert(t(messageKey));
};

const createConnection = async () => {
  activeAction.value = 'create';
  try {
    const { data } = await naviOsMcpConnectionsAPI.create({
      connection: form.value,
    });
    issuedToken.value = data.token;
    useAlert(t('INTEGRATION_SETTINGS.NAVI_OS_MCP.API.CREATE_SUCCESS'));
    await loadConnections();
  } catch (error) {
    useAlert(
      errorMessage(error, 'INTEGRATION_SETTINGS.NAVI_OS_MCP.API.CREATE_ERROR')
    );
  } finally {
    activeAction.value = '';
  }
};

const rotateToken = async connection => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('INTEGRATION_SETTINGS.NAVI_OS_MCP.CONFIRM_ROTATE')))
    return;

  activeAction.value = `rotate-${connection.id}`;
  try {
    const { data } = await naviOsMcpConnectionsAPI.rotateToken(connection.id);
    issuedToken.value = data.token;
    useAlert(t('INTEGRATION_SETTINGS.NAVI_OS_MCP.API.ROTATE_SUCCESS'));
    await loadConnections();
  } catch (error) {
    useAlert(
      errorMessage(error, 'INTEGRATION_SETTINGS.NAVI_OS_MCP.API.ACTION_ERROR')
    );
  } finally {
    activeAction.value = '';
  }
};

const toggleStatus = async connection => {
  const action = connection.status === 'active' ? 'suspend' : 'resume';
  activeAction.value = `${action}-${connection.id}`;
  try {
    await naviOsMcpConnectionsAPI[action](connection.id);
    useAlert(t('INTEGRATION_SETTINGS.NAVI_OS_MCP.API.STATUS_SUCCESS'));
    await loadConnections();
  } catch (error) {
    useAlert(
      errorMessage(error, 'INTEGRATION_SETTINGS.NAVI_OS_MCP.API.ACTION_ERROR')
    );
  } finally {
    activeAction.value = '';
  }
};

const savePermissions = async connection => {
  activeAction.value = `permissions-${connection.id}`;
  try {
    await naviOsMcpConnectionsAPI.updatePermissions(connection.id, {
      scopes: connection.scopes,
      allowed_inbox_ids: connection.allowed_inbox_ids,
      allowed_funnel_ids: connection.allowed_funnel_ids,
    });
    useAlert(t('INTEGRATION_SETTINGS.NAVI_OS_MCP.API.PERMISSIONS_SUCCESS'));
    await loadConnections();
  } catch (error) {
    useAlert(
      errorMessage(error, 'INTEGRATION_SETTINGS.NAVI_OS_MCP.API.ACTION_ERROR')
    );
  } finally {
    activeAction.value = '';
  }
};

const revokeConnection = async connection => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('INTEGRATION_SETTINGS.NAVI_OS_MCP.CONFIRM_REVOKE')))
    return;

  activeAction.value = `revoke-${connection.id}`;
  try {
    await naviOsMcpConnectionsAPI.delete(connection.id);
    issuedToken.value = '';
    useAlert(t('INTEGRATION_SETTINGS.NAVI_OS_MCP.API.REVOKE_SUCCESS'));
    await loadConnections();
  } catch (error) {
    useAlert(
      errorMessage(error, 'INTEGRATION_SETTINGS.NAVI_OS_MCP.API.ACTION_ERROR')
    );
  } finally {
    activeAction.value = '';
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('INTEGRATION_SETTINGS.NAVI_OS_MCP.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.NAVI_OS_MCP.TITLE')"
        :description="$t('INTEGRATION_SETTINGS.NAVI_OS_MCP.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-6 pb-8">
        <section
          v-if="approvals.some(item => item.status === 'pending')"
          class="flex flex-col gap-3"
        >
          <h3 class="m-0 text-base font-semibold text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.APPROVALS') }}
          </h3>
          <article
            v-for="approval in approvals.filter(
              item => item.status === 'pending'
            )"
            :key="approval.id"
            class="flex flex-col gap-3 rounded-xl border border-n-amber-7 bg-n-amber-3 p-4"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="m-0 font-medium text-n-slate-12">
                  {{ approval.tool_name }}
                </p>
                <p class="mb-0 mt-1 text-xs text-n-slate-10">
                  {{ approval.requested_by }}
                </p>
              </div>
              <span class="text-xs text-n-slate-10">{{
                approval.request_id
              }}</span>
            </div>
            <pre
              class="m-0 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg bg-n-alpha-2 p-3 text-xs text-n-slate-11"
            ><code>{{ formatArguments(approval.arguments) }}</code></pre>
            <div class="flex gap-2">
              <Button
                icon="i-lucide-check"
                :label="$t('INTEGRATION_SETTINGS.NAVI_OS_MCP.APPROVE')"
                :is-loading="activeAction === `approve-${approval.id}`"
                @click="decideApproval(approval, 'approve')"
              />
              <Button
                outline
                ruby
                icon="i-lucide-x"
                :label="$t('INTEGRATION_SETTINGS.NAVI_OS_MCP.REJECT')"
                :is-loading="activeAction === `reject-${approval.id}`"
                @click="decideApproval(approval, 'reject')"
              />
            </div>
          </article>
        </section>
        <section
          class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-alpha-2 p-5"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.ENDPOINT') }}
          </span>
          <div class="flex flex-col gap-2 sm:flex-row">
            <input
              :value="endpoint"
              readonly
              class="min-w-0 flex-1 overflow-x-auto rounded-lg bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-11"
            />
            <Button
              outline
              icon="i-lucide-copy"
              :label="$t('INTEGRATION_SETTINGS.NAVI_OS_MCP.COPY_ENDPOINT')"
              @click="
                copy(
                  endpoint,
                  'INTEGRATION_SETTINGS.NAVI_OS_MCP.API.ENDPOINT_COPIED'
                )
              "
            />
          </div>
        </section>

        <section
          v-if="issuedToken"
          class="flex flex-col gap-3 rounded-xl border border-n-amber-7 bg-n-amber-3 p-5"
        >
          <div>
            <h3 class="m-0 text-base font-semibold text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.TOKEN_TITLE') }}
            </h3>
            <p class="mb-0 mt-1 text-sm text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.TOKEN_WARNING') }}
            </p>
          </div>
          <input
            :value="issuedToken"
            readonly
            class="overflow-x-auto rounded-lg border border-n-amber-7 bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
          />
          <Button
            class="w-fit"
            icon="i-lucide-copy"
            :label="$t('INTEGRATION_SETTINGS.NAVI_OS_MCP.COPY_TOKEN')"
            @click="
              copy(
                issuedToken,
                'INTEGRATION_SETTINGS.NAVI_OS_MCP.API.TOKEN_COPIED'
              )
            "
          />
        </section>

        <section v-if="hasConnections" class="flex flex-col gap-3">
          <h3 class="m-0 text-base font-semibold text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.CONNECTIONS') }}
          </h3>
          <article
            v-for="connection in connections"
            :key="connection.id"
            class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-alpha-2 p-5"
          >
            <div
              class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
            >
              <div>
                <h4 class="m-0 text-base font-semibold text-n-slate-12">
                  {{ connection.name }}
                </h4>
                <p class="mb-0 mt-1 text-sm text-n-slate-10">
                  {{
                    $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.IDENTITY', {
                      installation: connection.installation_id,
                      user: connection.user.name,
                    })
                  }}
                </p>
              </div>
              <span
                class="w-fit rounded-full px-2.5 py-1 text-xs font-medium"
                :class="
                  connection.status === 'active'
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-10'
                "
              >
                {{ connection.status }}
              </span>
            </div>

            <fieldset class="flex flex-col gap-2">
              <legend class="mb-1 text-sm font-medium text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.PERMISSIONS') }}
              </legend>
              <label
                v-for="scope in scopeOptions"
                :key="scope"
                class="flex items-center gap-2 text-sm text-n-slate-11"
              >
                <input
                  v-model="connection.scopes"
                  type="checkbox"
                  :value="scope"
                  :disabled="connection.status === 'revoked'"
                />
                <span>{{ scope }}</span>
              </label>
            </fieldset>

            <div class="grid gap-4 border-t border-n-weak pt-4 sm:grid-cols-2">
              <fieldset class="flex flex-col gap-2">
                <legend class="mb-1 text-sm font-medium text-n-slate-12">
                  {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.ALLOWED_INBOXES') }}
                </legend>
                <label
                  v-for="inbox in availableInboxes"
                  :key="inbox.id"
                  class="flex items-center gap-2 text-sm text-n-slate-11"
                >
                  <input
                    v-model="connection.allowed_inbox_ids"
                    type="checkbox"
                    :value="inbox.id"
                    :disabled="connection.status === 'revoked'"
                  />
                  <span>{{ inbox.name }}</span>
                </label>
              </fieldset>
              <fieldset class="flex flex-col gap-2">
                <legend class="mb-1 text-sm font-medium text-n-slate-12">
                  {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.ALLOWED_FUNNELS') }}
                </legend>
                <label
                  v-for="funnel in availableFunnels"
                  :key="funnel.id"
                  class="flex items-center gap-2 text-sm text-n-slate-11"
                >
                  <input
                    v-model="connection.allowed_funnel_ids"
                    type="checkbox"
                    :value="funnel.id"
                    :disabled="connection.status === 'revoked'"
                  />
                  <span>{{ funnel.name }}</span>
                </label>
              </fieldset>
            </div>

            <Button
              outline
              class="w-fit"
              icon="i-lucide-save"
              :label="$t('INTEGRATION_SETTINGS.NAVI_OS_MCP.SAVE_PERMISSIONS')"
              :disabled="connection.status === 'revoked'"
              :is-loading="activeAction === `permissions-${connection.id}`"
              @click="savePermissions(connection)"
            />

            <p class="m-0 text-xs text-n-slate-10">
              {{
                $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.ACCESS_SUMMARY', {
                  inboxes: connection.allowed_inbox_ids.length,
                  funnels: connection.allowed_funnel_ids.length,
                  hint: connection.token_hint,
                })
              }}
            </p>

            <div class="flex flex-wrap gap-2 border-t border-n-weak pt-4">
              <Button
                outline
                icon="i-lucide-refresh-cw"
                :label="$t('INTEGRATION_SETTINGS.NAVI_OS_MCP.ROTATE')"
                :is-loading="activeAction === `rotate-${connection.id}`"
                @click="rotateToken(connection)"
              />
              <Button
                outline
                :icon="
                  connection.status === 'active'
                    ? 'i-lucide-pause'
                    : 'i-lucide-play'
                "
                :label="
                  connection.status === 'active'
                    ? $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.SUSPEND')
                    : $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.RESUME')
                "
                :disabled="connection.status === 'revoked'"
                :is-loading="
                  activeAction === `suspend-${connection.id}` ||
                  activeAction === `resume-${connection.id}`
                "
                @click="toggleStatus(connection)"
              />
              <Button
                outline
                ruby
                icon="i-lucide-trash-2"
                :label="$t('INTEGRATION_SETTINGS.NAVI_OS_MCP.REVOKE')"
                :is-loading="activeAction === `revoke-${connection.id}`"
                @click="revokeConnection(connection)"
              />
            </div>
          </article>
        </section>

        <form
          class="flex flex-col gap-5 rounded-xl border border-n-weak bg-n-alpha-2 p-5"
          @submit.prevent="createConnection"
        >
          <div>
            <h3 class="m-0 text-base font-semibold text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.CREATE_TITLE') }}
            </h3>
            <p class="mb-0 mt-1 text-sm text-n-slate-10">
              {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.SEAT_WARNING') }}
            </p>
          </div>

          <div class="grid gap-4 sm:grid-cols-2">
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.NAME') }}
              </span>
              <input v-model="form.name" required class="form-input" />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.INSTALLATION_ID') }}
              </span>
              <input
                v-model="form.installation_id"
                class="form-input"
                :placeholder="
                  $t(
                    'INTEGRATION_SETTINGS.NAVI_OS_MCP.INSTALLATION_PLACEHOLDER'
                  )
                "
              />
            </label>
          </div>

          <fieldset class="flex flex-col gap-2">
            <legend class="mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.PERMISSIONS') }}
            </legend>
            <label
              v-for="scope in scopeOptions"
              :key="scope"
              class="flex items-center gap-2 text-sm text-n-slate-11"
            >
              <input v-model="form.scopes" type="checkbox" :value="scope" />
              <span>{{ scope }}</span>
            </label>
          </fieldset>

          <div class="grid gap-4 sm:grid-cols-2">
            <fieldset class="flex flex-col gap-2">
              <legend class="mb-1 text-sm font-medium text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.ALLOWED_INBOXES') }}
              </legend>
              <label
                v-for="inbox in availableInboxes"
                :key="inbox.id"
                class="flex items-center gap-2 text-sm text-n-slate-11"
              >
                <input
                  v-model="form.allowed_inbox_ids"
                  type="checkbox"
                  :value="inbox.id"
                />
                <span>{{ inbox.name }}</span>
              </label>
            </fieldset>
            <fieldset class="flex flex-col gap-2">
              <legend class="mb-1 text-sm font-medium text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.NAVI_OS_MCP.ALLOWED_FUNNELS') }}
              </legend>
              <label
                v-for="funnel in availableFunnels"
                :key="funnel.id"
                class="flex items-center gap-2 text-sm text-n-slate-11"
              >
                <input
                  v-model="form.allowed_funnel_ids"
                  type="checkbox"
                  :value="funnel.id"
                />
                <span>{{ funnel.name }}</span>
              </label>
            </fieldset>
          </div>

          <Button
            type="submit"
            class="w-fit"
            icon="i-lucide-plus"
            :label="$t('INTEGRATION_SETTINGS.NAVI_OS_MCP.CREATE')"
            :disabled="!form.scopes.length"
            :is-loading="activeAction === 'create'"
          />
        </form>
      </div>
    </template>
  </SettingsLayout>
</template>
